import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
type ChoiceCardProps = {
  title: string;
  onClick: () => void;
  primary?: boolean;
  icon?: React.ReactNode;
};

// One option row: optional leading icon + title + trailing arrow. The icon is
// only used when it adds meaning the label doesn't already carry.
function ChoiceCard({
  title,
  onClick,
  primary = false,
  icon
}: ChoiceCardProps) {
  return <button type="button" onClick={onClick} className={`group flex w-full items-center gap-2.5 rounded-xl border px-5 py-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#EA580C] focus-visible:ring-offset-2 ${primary ? 'border-[#2B211A] bg-[#2B211A] text-white hover:bg-[#1A1410]' : 'border-neutral-200 bg-white text-[#2B211A] hover:border-[#CFC6BB] hover:bg-[#FFFCF8]'}`} data-id="element-13217" data-mp="fugdigugfrtl">
      
      {icon && <span className={primary ? 'text-white/80' : 'text-[#EA580C]'} data-id="element-13218" data-mp="3fxidqarunzoa">{icon}</span>}
      <span className="flex-1 text-[15px] font-bold leading-tight" data-id="element-13219" data-mp="3fvdy4vkl9c86">{title}</span>
      <ArrowRight size={17} className={`shrink-0 transition-transform group-hover:translate-x-0.5 ${primary ? 'text-white/80' : 'text-neutral-400 group-hover:text-[#EA580C]'}`} data-id="element-13220" data-mp="3jyk565fiz7fp" />
      
    </button>;
}
export function WorkspaceAlreadyConnected() {
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();
  return <main className="min-h-screen bg-[#FAF8F4] font-body text-[#2B211A] selection:bg-[#EA580C]/20" data-id="element-13221" data-mp="2w7oe5aiw8g9i">
      {/* No header logo — the Giga mark already appears inside the card's
       connection visual, and showing it twice on one screen felt redundant.
       A quiet "Back to home" escape hatch sits top-left instead, matching
       the signup flow's convention. */}
      <Link to="/" className="absolute z-20 top-6 left-6 flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-[#2B211A] transition-colors" data-id="element-13222" data-mp="2db6r36ezp4jl">
        
        <ArrowLeft size={16} data-id="element-13223" data-mp="1z5d3tyirrc6r" />
        Back to home
      </Link>

      <div className="mx-auto flex min-h-screen w-full max-w-xl flex-col px-5 pt-12 pb-8 sm:px-6" data-id="element-13224" data-mp="1ueen4xtvcc2z">
        <motion.div initial={reducedMotion ? false : {
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: reducedMotion ? 0 : 0.36,
        ease: [0.22, 1, 0.36, 1]
      }} className="flex flex-1 items-center py-10" data-id="element-13225" data-mp="1pfbihehsw7vj">
          
          <div className="w-full" data-id="element-13226" data-mp="2d55tdqik7md5">
            <div className="rounded-[28px] border border-neutral-200 bg-white px-6 py-8 shadow-[0_28px_70px_-42px_rgba(43,33,26,0.34)] sm:px-10 sm:py-10" data-id="element-13227" data-mp="2d5xmmuoop809">
              {/* Connection visual — Giga and the Slack workspace tile joined
               by a dashed link, with the Connected pill sitting ON the link
               itself. One visual tells the whole story: this workspace and
               Giga are already linked. */}
              <div className="flex items-center justify-center" aria-hidden="true" data-id="element-13228" data-mp="rtnvfk78j1wo">
                {/* Giga app tile — brand orange with the flower knocked out
                 to white, mirroring the dark Slack workspace tile beside it. */}
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EA580C]" data-id="element-13229" data-mp="3dckni6i4da21">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" className="h-7 w-7 object-contain [filter:brightness(0)_invert(1)]" data-id="element-13230" data-mp="3i42oguk57an0" />
                  
                </span>

                <span className="h-px w-7 border-t-2 border-dashed border-neutral-200 sm:w-10" data-id="element-13231" data-mp="3w32fg8v4o1mc" />
                <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#EEF8F1] px-3 py-1.5 text-xs font-bold text-[#207A43]" data-id="element-13232" data-mp="4xsw301n2llp">
                  <Check size={13} strokeWidth={3} data-id="element-13233" data-mp="21rdw7uhv5cw5" />
                  Connected
                </span>
                <span className="h-px w-7 border-t-2 border-dashed border-neutral-200 sm:w-10" data-id="element-13234" data-mp="26ow6bo7m9y5q" />

                {/* The workspace's real Slack icon (from team.info in
                 production), with the Slack mark badged on the corner. */}
                <span className="relative shrink-0" data-id="element-13235" data-mp="a1jy5cfgbph9">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/d266543a-4fbf-4936-bffe-2b72688d1870.jpg" alt="" className="h-14 w-14 rounded-2xl border border-neutral-200 object-cover" data-id="element-13236" data-mp="a08s15yja5mz" />
                  
                  <span className="absolute -bottom-1.5 -right-1.5 flex h-6 w-6 items-center justify-center rounded-lg border border-neutral-100 bg-white shadow-sm" data-id="element-13237" data-mp="39e2glxgbasmb">
                    <img src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" alt="" className="h-3.5 w-3.5 object-contain" onError={e => {
                    const img = e.currentTarget as HTMLImageElement;
                    if (!img.dataset.fallback) {
                      img.dataset.fallback = '1';
                      img.src = 'https://cdn.simpleicons.org/slack';
                    }
                  }} data-id="element-13238" data-mp="3ifsglro9ezvp" />
                    
                  </span>
                </span>
              </div>

              {/* Status block: headline + supporting line, closed off as its
               own thought. */}
              <div className="mt-8 text-center" data-id="element-13239" data-mp="2bkb8yc3gmrgi">
                <h1 className="text-2xl font-title font-bold leading-tight tracking-tight text-[#2B211A] sm:text-[28px]" data-id="element-13240" data-mp="2zd9x0aznwc0x">
                  Giga Main is already connected
                </h1>
                <p className="mt-2 text-[15px] text-neutral-500" data-id="element-13241" data-mp="2peu2rz5c8mn7">
                  No need to set it up again.
                </p>
              </div>

              {/* Decision block: the question is a LABEL for the buttons, so
               it's grouped tightly with them (big gap above, small gap
               below) — proximity tells the user what it belongs to. */}
              <div className="mt-9" data-id="element-13242" data-mp="3lkdzvmu02j44">
                <p className="mb-3 text-left text-[13px] font-bold uppercase tracking-[0.12em] text-neutral-400" data-id="element-13243" data-mp="3ljm5ugg1wtfy">
                  What would you like to do?
                </p>
                <div className="space-y-3" data-id="element-13244" data-mp="3buazv2ogo4wa">
                  {/* No leading icon on the primary — the workspace avatar
                   already identifies "Giga Main" in the visual just above,
                   and repeating it here read as redundant. */}
                  <ChoiceCard primary title="Continue with Giga Main" onClick={() => navigate('/app-screen-mockups/dashboard')} data-id="element-13245" data-mp="31ubo6wwqvyf7" />
                  
                  <ChoiceCard title="Create a separate workspace" icon={<Plus size={17} data-id="element-13247" data-mp="2sxt84l9m4501" />} onClick={() => navigate('/app-screen-mockups/signup?workspace=new')} data-id="element-13246" data-mp="2sx1c59c3s598" />
                  
                </div>
              </div>
            </div>

            <p className="mt-5 px-5 text-center text-xs leading-relaxed text-neutral-400" data-id="element-13248" data-mp="r2iv05mlupyv">
              Need access to Giga Main? Ask a workspace admin to invite you.
            </p>
          </div>
        </motion.div>
      </div>
    </main>;
}